import { isLocale, type Locale } from '@unibyte/contracts/locales';

export interface PublicWidgetConfig {
  configVersion: number;
  title: string;
  defaultLocale: Locale;
  position: 'left' | 'right';
  accent: string | null;
  logoUrl: string | null;
  privacyUrl: string | null;
  contactUrl: string | null;
  httpTimeoutMs: number;
  showAnswerFeedback: boolean;
  locales: Array<{
    locale: Locale;
    greeting: string;
    starterQuestions: Array<{
      question: string;
      answer: string;
      icon: 'QUESTION' | 'INFO' | 'SERVICES' | 'PRICE' | 'CLOCK' | 'CONTACT';
      action: 'ANSWER' | 'CONTACT' | 'QUOTE';
    }>;
  }>;
}

export interface BootstrapHandoff {
  type: 'unibyte-chat:bootstrap';
  ticket: string;
  config: PublicWidgetConfig;
}

export interface UnavailableHandoff {
  type: 'unibyte-chat:unavailable';
}

export interface VisibleHandoff {
  type: 'unibyte-chat:visible';
}

/** postMessage target. `*` is never a valid target for a ticket or a session signal. */
export function exactTarget(origin: string): string | null {
  if (!origin || origin === '*') return null;
  try {
    const url = new URL(origin);
    if (url.origin !== origin) return null;
    if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;
    return url.origin;
  } catch {
    return null;
  }
}

export function readParentHandoff(input: {
  origin: string;
  source: unknown;
  parent: unknown;
  expectedOrigin: string | null;
  data: unknown;
}): BootstrapHandoff | UnavailableHandoff | VisibleHandoff | null {
  const expected = input.expectedOrigin ? exactTarget(input.expectedOrigin) : null;
  if (
    !expected ||
    input.origin !== expected ||
    input.source == null ||
    input.source !== input.parent
  ) {
    return null;
  }
  if (!input.data || typeof input.data !== 'object') return null;
  const record = input.data as Record<string, unknown>;
  if (record['type'] === 'unibyte-chat:unavailable') return { type: 'unibyte-chat:unavailable' };
  if (record['type'] === 'unibyte-chat:visible') return { type: 'unibyte-chat:visible' };
  if (record['type'] !== 'unibyte-chat:bootstrap' || typeof record['ticket'] !== 'string')
    return null;
  if (record['ticket'].length < 20 || record['ticket'].includes(' ')) return null;
  const config = readConfig(record['config']);
  if (!config) return null;
  return { type: 'unibyte-chat:bootstrap', ticket: record['ticket'], config };
}

export function readChildMessage(input: {
  origin: string;
  source: unknown;
  frame: unknown;
  widgetOrigin: string;
  data: unknown;
}): 'ready' | 'close' | 'reconnect' | null {
  const expected = exactTarget(input.widgetOrigin);
  if (
    !expected ||
    input.origin !== expected ||
    input.source == null ||
    input.source !== input.frame
  ) {
    return null;
  }
  if (!input.data || typeof input.data !== 'object') return null;
  const type = (input.data as { type?: unknown }).type;
  if (type === 'unibyte-chat:ready') return 'ready';
  if (type === 'unibyte-chat:close') return 'close';
  if (type === 'unibyte-chat:reconnect') return 'reconnect';
  return null;
}

function readConfig(value: unknown): PublicWidgetConfig | null {
  if (!value || typeof value !== 'object') return null;
  const record = value as Record<string, unknown>;
  if (typeof record['title'] !== 'string' || !isLocale(record['defaultLocale'])) return null;
  if (record['position'] !== 'left' && record['position'] !== 'right') return null;
  if (!Array.isArray(record['locales'])) return null;
  const locales: PublicWidgetConfig['locales'] = [];
  for (const entry of record['locales']) {
    if (!entry || typeof entry !== 'object') return null;
    const locale = (entry as { locale?: unknown }).locale;
    const greeting = (entry as { greeting?: unknown }).greeting;
    if (!isLocale(locale) || typeof greeting !== 'string') return null;
    const rawStarters = (entry as { starterQuestions?: unknown }).starterQuestions;
    const starterQuestions = Array.isArray(rawStarters)
      ? rawStarters.slice(0, 6).flatMap((starter) => {
          if (!starter || typeof starter !== 'object') return [];
          const question = (starter as { question?: unknown }).question;
          const answer = (starter as { answer?: unknown }).answer;
          const rawIcon = (starter as { icon?: unknown }).icon;
          const rawAction = (starter as { action?: unknown }).action;
          if (typeof question !== 'string') return [];
          const cleanQuestion = question.trim().slice(0, 160);
          const cleanAnswer = typeof answer === 'string' ? answer.trim().slice(0, 2000) : '';
          const action: PublicWidgetConfig['locales'][number]['starterQuestions'][number]['action'] =
            rawAction === 'CONTACT' || rawAction === 'QUOTE' || rawAction === 'ANSWER'
              ? rawAction
              : 'ANSWER';
          const icon: PublicWidgetConfig['locales'][number]['starterQuestions'][number]['icon'] =
            rawIcon === 'INFO' ||
            rawIcon === 'SERVICES' ||
            rawIcon === 'PRICE' ||
            rawIcon === 'CLOCK' ||
            rawIcon === 'CONTACT' ||
            rawIcon === 'QUESTION'
              ? rawIcon
              : 'QUESTION';
          return cleanQuestion && (action !== 'ANSWER' || cleanAnswer)
            ? [{ question: cleanQuestion, answer: cleanAnswer, icon, action }]
            : [];
        })
      : [];
    locales.push({ locale, greeting, starterQuestions });
  }
  const accent =
    typeof record['accent'] === 'string' && /^#[0-9a-fA-F]{6}$/.test(record['accent'])
      ? record['accent']
      : null;
  const logoUrl =
    typeof record['logoUrl'] === 'string' &&
    (record['logoUrl'].startsWith('https://') || record['logoUrl'].startsWith('http://'))
      ? record['logoUrl']
      : null;
  const privacyUrl = typeof record['privacyUrl'] === 'string' ? record['privacyUrl'] : null;
  const contactUrl = typeof record['contactUrl'] === 'string' ? record['contactUrl'] : null;
  const httpTimeoutMs =
    typeof record['httpTimeoutMs'] === 'number' &&
    Number.isFinite(record['httpTimeoutMs']) &&
    record['httpTimeoutMs'] >= 1_000
      ? Math.floor(record['httpTimeoutMs'])
      : 20_000;
  const showAnswerFeedback = record['showAnswerFeedback'] !== false;
  return {
    configVersion: typeof record['configVersion'] === 'number' ? record['configVersion'] : 0,
    title: record['title'],
    defaultLocale: record['defaultLocale'],
    position: record['position'],
    accent,
    logoUrl,
    privacyUrl,
    contactUrl,
    httpTimeoutMs,
    showAnswerFeedback,
    locales,
  };
}
